import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { SettlingInSettingsService } from '@/api';
import type { UpdateIndicatorsRequest } from '@/types/settlingIn';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { getErrorMessage } from '@/utils/errorUtils';

// Query key factory for settling-in settings
export const settlingInSettingsKeys = {
  all: ['settlingInSettings'] as const,
  indicators: () => [...settlingInSettingsKeys.all, 'indicators'] as const,
};

/**
 * Hook to fetch settling-in indicator definitions.
 */
export function useSettlingInIndicators() {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: settlingInSettingsKeys.indicators(),
    queryFn: () => SettlingInSettingsService.getApiV1SettingsSettlingInIndicators(),
    enabled: isSignedIn && isAuthReady,
    staleTime: 60000,
  });
}

/**
 * Hook to update settling-in indicator definitions.
 */
export function useUpdateSettlingInIndicators() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: (data: UpdateIndicatorsRequest) =>
      SettlingInSettingsService.putApiV1SettingsSettlingInIndicators(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlingInSettingsKeys.all });
      toast.success(t({ id: 'settlingIn.settings.success.updated', message: 'Indicators updated successfully' }));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t({ id: 'settlingIn.settings.error.updateFailed', message: 'Failed to update indicators' })));
    },
  });
}
